import { Axios } from './ApiService';
import { ValidationDataSet } from './Constants';
import { DuplicateValidationFormMap } from './Models';

/**
 * Performs a duplicate validation check for a specific form section.
 *
 * This function sends a search request to the given API endpoint using the
 * primary (unique) field defined in the `ValidationDataSet` for the provided type.
 * The response is expected to return an array of objects, each representing an
 * existing record in the system. These records are checked against `formValue`
 * to determine whether a duplicate entry already exists.
 *
 * @template T - A key of `ValidationDataSet` that determines how validation is performed.
 *
 * @param formValue - The form data object corresponding to the selected type.
 * It must match the structure of `DuplicateValidationFormMap[T]`.
 *
 * @param endpoint - The API endpoint that performs the duplicate lookup.
 *
 * @param type - The form section key (e.g., `"service"`, `"packagingType"`)
 * used to determine which validation rules in `ValidationDataSet` to apply.
 *
 * @returns A promise resolving to:
 * - `true` if a duplicate record is found
 * - `false` if no duplicates exist or validation fails
 *
 * @description
 * Validation logic:
 * - Always checks the primary field (e.g., `name`, `color`, etc.)
 * - If secondary fields exist (e.g., `packageSizeId`), those must also match.
 * - Case-insensitive comparison is applied for string values.
 *
 * @throws Returns `false` if the API request fails.
 */
export const DuplicateValidation = async <
  T extends keyof typeof ValidationDataSet,
>(
  formValue: DuplicateValidationFormMap[T],
  endpoint: string,
  type: T,
): Promise<boolean> => {
  try {
    const {
      arrayKey,
      primarySearchField,
      SecondarySearchField = [],
    } = ValidationDataSet[type];

    // Send search request to the API using the primary field value as search key
    const response = await Axios.post(endpoint, {
      search: formValue[primarySearchField],
    });

    // Extract the data array from the API response
    const dataList = response.data?.data?.[arrayKey];

    // Comparing the primary field of the record with the form value (user entered)
    const matchesprimarySearchField = (data: DuplicateValidationFormMap[T]) =>
      String(data[primarySearchField]).toLowerCase().trim() ===
      String(formValue[primarySearchField]).toLowerCase().trim();

    // Return true if any record matches the primary field when no secondary fields exist
    if (SecondarySearchField.length === 0) {
      return dataList.some(matchesprimarySearchField); // returns true
    }

    // Return true if any record matches both primary and all secondary fields
    // This is for Packaging type - package size validation
    return dataList.some((data: DuplicateValidationFormMap[T]) => {
      // To ensure the primary field always matches before checking secondary fields
      if (!matchesprimarySearchField(data)) return false;
      return SecondarySearchField.every(
        (key) => String(data[key]) === String(formValue[key]),
      );
    });
  } catch (error) {
    console.log('Error validating record', error);
    return false;
  }
};
